import {
    useCallback,
    useEffect,
    useState,
    useMemo,
    type FC,
    useRef
} from "react";
import {
    type LayoutChangeEvent,
    Platform,
    View
} from "react-native";
import type IMenubarProps from "./type";
import type {
    MenubarRegistryEntryType,
    MenubarCloseIntentType,
    MenubarOpenModeType,
    MenubarMoveStepType,
    MenubarContextType
} from "./type";
import stylesheet, {
    getMenubarPlainOffset,
    useStyles
} from "./stylesheet";
import MenubarContent from "./components/content";
import MenubarMenu from "./components/menu";
import MenubarTrigger from "./components/trigger";
import {
    MenubarContext
} from "./context";
import {
    MENUBAR_SURFACE_SELECTOR,
    MENUBAR_ESCAPE_KEY,
    MENUBAR_ALT_KEY,
    MENUBAR_F10_KEY,
    getMenubarMenus,
    getMenubarKeys
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../types";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    Ellipsis as EllipsisIcon
} from "lucide-react-native";
import {
    getIsDropdownMenuKeyboardFocus,
    getDropdownMenuActiveElement,
    focusDropdownMenuElement,
    getDropdownMenuElement
} from "../dropdownMenu/util";
import {
    splitControlStyle
} from "../../helpers/theme/control";
import {
    getHorizontalStep
} from "../../helpers/direction";
import {
    getKbdPlatform
} from "../kbd/util";
import AnimatedIcon from "../animatedIcon";
import DropdownMenu from "../dropdownMenu";
import Button from "../button";

const renderMoreIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => {
    return <AnimatedIcon
        customColor={customColor}
        animationTrigger="hover"
        animation="signature"
        icon={EllipsisIcon}
        color={color}
        size={size}
    />;
};

const Menubar: FC<IMenubarProps> = ({
    collapseBehaviour = "auto",
    "aria-label": ariaLabel,
    openMenu: openMenuProp,
    variant = "plain",
    customLocalize,
    onOpenChange,
    customTheme,
    children,
    style,
    ...props
}) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        shadowAlpha,
        elevations,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const barRef = useRef<View | null>(null);

    const registryRef = useRef(new Map<string, MenubarRegistryEntryType>());

    const previousFocusRef = useRef<HTMLElement | null>(null);

    const closeIntentRef = useRef<MenubarCloseIntentType>("previous");

    const isEscapeRef = useRef(false);

    const wasOpenRef = useRef(false);

    const openKeyRef = useRef<string | null>(null);

    const tabKeyRef = useRef<string | null>(null);

    const [
        openState,
        setOpenState
    ] = useState<string | null>(null);

    const [
        openMode,
        setOpenMode
    ] = useState<MenubarOpenModeType>("pointer");

    const [
        tabKey,
        setTabKey
    ] = useState<string | null>(null);

    const [
        availableWidth,
        setAvailableWidth
    ] = useState(0);

    const [
        naturalWidth,
        setNaturalWidth
    ] = useState(0);

    const openKey = openMenuProp !== undefined ? openMenuProp : openState;

    openKeyRef.current = openKey;

    tabKeyRef.current = tabKey;

    const {
        containerStyle,
        surfaceStyle
    } = splitControlStyle({
        style
    });

    const {
        bar: barDynamicStyle
    } = useStyles({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        shadowAlpha,
        elevations,
        radiuses,
        borders,
        variant,
        colors,
        spaces
    });

    const plainOffset = variant === "plain" ? getMenubarPlainOffset({
        controlPaddings,
        controlHeights
    }) : 0;

    const isOverflow = naturalWidth > 0 && availableWidth > 0 && naturalWidth - plainOffset > availableWidth;

    const isCollapsed = collapseBehaviour === "always" || (collapseBehaviour === "auto" && (pointerType === "coarse" || isOverflow));

    const handleOpenChange = useCallback((menuKey: string | null, nextOpenMode?: MenubarOpenModeType) => {
        if(menuKey === openKeyRef.current) {
            return;
        }

        if(menuKey !== null) {
            setOpenMode(nextOpenMode ?? (getIsDropdownMenuKeyboardFocus(getDropdownMenuActiveElement()) ? "first" : "pointer"));

            setTabKey(menuKey);
        }

        openKeyRef.current = menuKey;

        setOpenState(menuKey);

        onOpenChange?.(menuKey);
    }, [
        onOpenChange
    ]);

    const handleFocusTrigger = useCallback((menuKey: string | null) => {
        if(menuKey === null) {
            return;
        }

        focusDropdownMenuElement(registryRef.current.get(menuKey)?.node);
    }, []);

    const handleRestorePrevious = useCallback(() => {
        const previousElement = previousFocusRef.current;

        if(previousElement?.isConnected) {
            focusDropdownMenuElement(previousElement);

            return;
        }

        getDropdownMenuActiveElement()?.blur();
    }, []);

    const handleClose = useCallback((menuKey: string) => {
        if(openKeyRef.current !== menuKey) {
            return;
        }

        closeIntentRef.current = isEscapeRef.current ? "trigger" : "previous";

        isEscapeRef.current = false;

        handleOpenChange(null);
    }, [
        handleOpenChange
    ]);

    const handleTabKey = useCallback((menuKey: string) => {
        if(openKeyRef.current !== menuKey) {
            return;
        }

        handleFocusTrigger(menuKey);

        closeIntentRef.current = "trigger";

        handleOpenChange(null);
    }, [
        handleFocusTrigger,
        handleOpenChange
    ]);

    const handleRegister = useCallback((menuKey: string, entry: MenubarRegistryEntryType) => {
        registryRef.current.set(menuKey, entry);

        setTabKey((previousKey) => previousKey ?? menuKey);
    }, []);

    const handleUnregister = useCallback((menuKey: string) => {
        registryRef.current.delete(menuKey);

        setTabKey((previousKey) => previousKey === menuKey ? null : previousKey);
    }, []);

    const handleTriggerFocus = useCallback((menuKey: string) => {
        setTabKey(menuKey);
    }, []);

    const handleMove = useCallback((menuKey: string, step: MenubarMoveStepType) => {
        const keys = getMenubarKeys({
            registry: registryRef.current
        });

        if(!keys.length) {
            return;
        }

        const index = keys.indexOf(menuKey);

        const nextIndex = step === "first" ? 0 : step === "last" ? keys.length - 1 : index < 0 ? 0 : (index + step + keys.length) % keys.length;

        const nextKey = keys[nextIndex] ?? menuKey;

        const nextEntry = registryRef.current.get(nextKey);

        setTabKey(nextKey);

        if(openKeyRef.current !== null && !nextEntry?.isDisabled) {
            handleOpenChange(nextKey, "first");

            return;
        }

        if(openKeyRef.current !== null) {
            closeIntentRef.current = "trigger";

            handleOpenChange(null);
        }

        focusDropdownMenuElement(nextEntry?.node);
    }, [
        handleOpenChange
    ]);

    const handleFocusBar = useCallback(() => {
        if(openKeyRef.current !== null) {
            return;
        }

        const keys = getMenubarKeys({
            registry: registryRef.current
        });

        handleFocusTrigger(tabKeyRef.current ?? keys[0] ?? null);
    }, [
        handleFocusTrigger
    ]);

    const handleBarLayout = (event: LayoutChangeEvent) => {
        setNaturalWidth(event.nativeEvent.layout.width);
    };

    const handleContainerLayout = (event: LayoutChangeEvent) => {
        setAvailableWidth(event.nativeEvent.layout.width);
    };

    const handleNoop = () => {};

    useEffect(() => {
        if(openKey !== null) {
            wasOpenRef.current = true;

            return;
        }

        if(!wasOpenRef.current) {
            return;
        }

        wasOpenRef.current = false;

        const closeIntent = closeIntentRef.current;

        closeIntentRef.current = "previous";

        if(Platform.OS !== "web") {
            return;
        }

        const activeElement = getDropdownMenuActiveElement();

        const isFocusInsideMenu = !!activeElement?.closest(MENUBAR_SURFACE_SELECTOR);

        const isFocusLost = !activeElement || activeElement === document.body;

        if(!isFocusInsideMenu && !isFocusLost) {
            return;
        }

        if(closeIntent === "trigger") {
            handleFocusTrigger(tabKeyRef.current);

            return;
        }

        handleRestorePrevious();
    }, [
        handleRestorePrevious,
        handleFocusTrigger,
        openKey
    ]);

    useEffect(() => {
        if(Platform.OS !== "web" || typeof document === "undefined" || isCollapsed) {
            return;
        }

        const platform = getKbdPlatform();

        let isAltAlone = false;

        const onFocusIn = (event: FocusEvent) => {
            const target = getDropdownMenuElement(event.target);

            if(!target || getDropdownMenuElement(barRef.current)?.contains(target) || target.closest(MENUBAR_SURFACE_SELECTOR)) {
                return;
            }

            previousFocusRef.current = target;
        };

        const onKeyDownCapture = (event: KeyboardEvent) => {
            isEscapeRef.current = event.key === MENUBAR_ESCAPE_KEY;
        };

        const onPointerDown = () => {
            isAltAlone = false;
        };

        const onKeyUp = (event: KeyboardEvent) => {
            if(event.key === MENUBAR_ALT_KEY && isAltAlone && platform === "other") {
                event.preventDefault();

                handleFocusBar();
            }

            isAltAlone = false;
        };

        const onKeyDown = (event: KeyboardEvent) => {
            isAltAlone = event.key === MENUBAR_ALT_KEY;

            if(event.defaultPrevented || event.key === MENUBAR_ALT_KEY) {
                return;
            }

            const isModified = event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;

            if(isModified) {
                return;
            }

            if(event.key === MENUBAR_F10_KEY) {
                event.preventDefault();

                handleFocusBar();

                return;
            }

            const step = getHorizontalStep({
                key: event.key,
                direction
            });

            if(step === 0) {
                return;
            }

            const target = getDropdownMenuElement(event.target);

            const isInBar = !!target && !!getDropdownMenuElement(barRef.current)?.contains(target);

            const isInMenu = openKeyRef.current !== null && !!target?.closest(MENUBAR_SURFACE_SELECTOR);

            const fromKey = openKeyRef.current ?? tabKeyRef.current;

            if((!isInBar && !isInMenu) || fromKey === null) {
                return;
            }

            event.preventDefault();

            handleMove(fromKey, step);
        };

        document.addEventListener("focusin", onFocusIn);

        document.addEventListener("keydown", onKeyDownCapture, true);

        document.addEventListener("keydown", onKeyDown);

        document.addEventListener("keyup", onKeyUp);

        document.addEventListener("pointerdown", onPointerDown, true);

        return () => {
            document.removeEventListener("focusin", onFocusIn);

            document.removeEventListener("keydown", onKeyDownCapture, true);

            document.removeEventListener("keydown", onKeyDown);

            document.removeEventListener("keyup", onKeyUp);

            document.removeEventListener("pointerdown", onPointerDown, true);
        };
    }, [
        handleFocusBar,
        isCollapsed,
        handleMove,
        direction
    ]);

    const value = useMemo<MenubarContextType>(() => ({
        handleRestorePrevious,
        handleTriggerFocus,
        handleOpenChange,
        handleUnregister,
        customLocalize,
        handleRegister,
        handleTabKey,
        customTheme,
        handleClose,
        handleMove,
        openMode,
        openKey,
        variant,
        tabKey
    }), [
        handleRestorePrevious,
        handleTriggerFocus,
        handleUnregister,
        handleOpenChange,
        handleRegister,
        customLocalize,
        handleTabKey,
        handleClose,
        handleMove,
        customTheme,
        openMode,
        openKey,
        variant,
        tabKey
    ]);

    const renderCollapsed = () => {
        const menus = getMenubarMenus({
            contentType: MenubarContent,
            triggerType: MenubarTrigger,
            menuType: MenubarMenu,
            children
        });

        const handleCollapsedOpenChange = (isOpen: boolean) => {
            handleOpenChange(isOpen ? (menus.find((menu) => !menu.isDisabled)?.menuKey ?? null) : null);
        };

        return <DropdownMenu
            onOpenChange={handleCollapsedOpenChange}
            title={ariaLabel ?? localize("menu")}
            customLocalize={customLocalize}
            isOpen={openKey !== null}
            customTheme={customTheme}
        >
            <DropdownMenu.Trigger>
                <Button
                    variant={variant === "outline" ? "outline" : "ghost"}
                    accessibilityLabel={localize("more-actions")}
                    customTheme={customTheme}
                    icon={renderMoreIcon}
                    onPress={handleNoop}
                    type="neutral"
                />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
                {
                    menus.map((menu) => <DropdownMenu.Sub
                        key={menu.menuKey}
                    >
                        <DropdownMenu.SubTrigger
                            isDisabled={menu.isDisabled}
                            title={menu.title}
                        />
                        <DropdownMenu.SubContent>
                            {menu.content}
                        </DropdownMenu.SubContent>
                    </DropdownMenu.Sub>)
                }
            </DropdownMenu.Content>
        </DropdownMenu>;
    };

    const renderBar = () => {
        return <MenubarContext.Provider
            value={value}
        >
            <View
                {...props}
                onLayout={handleBarLayout}
                aria-label={ariaLabel}
                style={[
                    surfaceStyle,
                    stylesheet.bar,
                    barDynamicStyle
                ]}
                role="menubar"
                ref={barRef}
            >
                {children}
            </View>
        </MenubarContext.Provider>;
    };

    return <View
        onLayout={handleContainerLayout}
        style={[
            containerStyle,
            stylesheet.container
        ]}
    >
        {isCollapsed ? renderCollapsed() : renderBar()}
    </View>;
};
export default Object.assign(Menubar, {
    CheckboxItem: DropdownMenu.CheckboxItem,
    RadioGroup: DropdownMenu.RadioGroup,
    SubContent: DropdownMenu.SubContent,
    SubTrigger: DropdownMenu.SubTrigger,
    RadioItem: DropdownMenu.RadioItem,
    Separator: DropdownMenu.Separator,
    Shortcut: DropdownMenu.Shortcut,
    Status: DropdownMenu.Status,
    Empty: DropdownMenu.Empty,
    Group: DropdownMenu.Group,
    Label: DropdownMenu.Label,
    Content: MenubarContent,
    Item: DropdownMenu.Item,
    Trigger: MenubarTrigger,
    Sub: DropdownMenu.Sub,
    Menu: MenubarMenu
});
